import React, { useCallback, useState } from 'react';
import isValidDate from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import { DatePicker } from '@atlaskit/datetime-picker';
import { createLocalizationProvider } from '@atlaskit/locale';
import { useIntl } from '@atlassian/jira-intl';
import { standardizeLocale } from '@atlassian/jira-issue-format-date/src/common/utils.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import type { DefaultDateTimeProps } from '../../common/types.tsx';
import { StyledRow, ChildWrapper, FixedChildWrapper } from '../../common/ui/styled.tsx';
import messages from './messages.tsx';

type Props = DefaultDateTimeProps;

const ONE_DAY_IN_MILLISECONDS = 86400000;

const isDateValueInvalid = (value: string) => !isValidDate(parseISO(value)) && value !== '';

const BetweenDates = ({ value: { value1, value2 }, onChange }: Props): React.JSX.Element => {
	const locale = useLocale();
	const standardizedLocale = standardizeLocale(locale);
	const [localizationProvider] = useState(createLocalizationProvider(standardizedLocale));
	const { formatMessage } = useIntl();

	const onChangeValue1Date = useCallback(
		(value: string) => {
			onChange({ value1: value, value2 });
		},
		[onChange, value2],
	);

	const onChangeValue2Date = useCallback(
		(value: string) => {
			onChange({ value1, value2: value });
		},
		[onChange, value1],
	);

	return (
		<StyledRow>
			<ChildWrapper>
				<DatePicker
					id="value1"
					locale={standardizedLocale}
					onChange={onChangeValue1Date}
					isInvalid={isDateValueInvalid(value1)}
					value={!isDateValueInvalid(value1) ? value1 : ''}
					spacing="compact"
					testId="jql-builder-basic-datetime.ui.between-dates.value-1"
					label={formatMessage(messages.startDateLabel)}
					placeholder={localizationProvider.formatDate(
						new Date(new Date().getTime() - ONE_DAY_IN_MILLISECONDS),
					)}
					shouldShowCalendarButton
					inputLabel={formatMessage(messages.startDateLabel)}
				/>
			</ChildWrapper>
			<FixedChildWrapper>{formatMessage(messages.and)}</FixedChildWrapper>
			<ChildWrapper>
				<DatePicker
					id="value2"
					locale={standardizedLocale}
					isInvalid={isDateValueInvalid(value2)}
					onChange={onChangeValue2Date}
					value={!isDateValueInvalid(value2) ? value2 : ''}
					spacing="compact"
					testId="jql-builder-basic-datetime.ui.between-dates.value-2"
					label={formatMessage(messages.endDateLabel)}
					placeholder={localizationProvider.formatDate(
						new Date(new Date().getTime() + ONE_DAY_IN_MILLISECONDS),
					)}
					shouldShowCalendarButton
					inputLabel={formatMessage(messages.endDateLabel)}
				/>
			</ChildWrapper>
		</StyledRow>
	);
};

export default BetweenDates;
